import React, { useState, ReactNode, useEffect } from 'react'
import { useCommitedState, useBrowserPrompt, RouterPromptModal } from '../blockers';
import { AlertProvider, ProgressBar, Spinner } from '../layout'
import { FormContext, FileDownloadFormats} from './Types';
import { ButtonDropdownItem } from '../buttons';

export interface FormProps<M> {
    onLoad: () => Promise<[M, number]>;
    onSubmit: (m: M) => Promise<M>;
    getNextPage: (page: number, m: M) => number;
    getPreviousPage: (page: number, m: M) => number;
    onExit: (m: M, submitted: boolean)=> number | null | undefined;
    onPrint?: (m: M, format: FileDownloadFormats) => Promise<any>;
    onSave?: (m: M) => Promise<M>;
    children?: (ctx: FormContext<M> | null) => ReactNode;
    progress?: (page: number, m: M | null) => {current: number, total: number};
    readOnly?: boolean;
    additionalDownloadButtons?: ButtonDropdownItem[];
}

export const Form = <M extends {}>(props: FormProps<M>) => {
    const [model, setModel] = useState<M | null>(null);
    const [loadingText, setLoadingText] = useState<string | null>(null);
    const [errorText, setErrorText] = useState<string | null>(null);

    const load = async ()=>{
        setErrorText(null);
        setLoadingText("Loading...")
        try {
            const [m, p] = await props.onLoad();
            onChange(m, true)
            setPage(p);
        }
        catch (e: any) {
            setErrorText(e.message ? e.message : "Unexpected error");
        }
        finally {
            setLoadingText(null);
        }
        return null;
    }

    useEffect(()=>{
        load();
    }, [])

    // Configure state and commited state
    const [commit, isUncommited] = useCommitedState(model);
    const onChange = (model: M, isCommit?: boolean) => {
        setModel(model);
        if (isCommit) {
            commit();
        }
    }

    // Configure browser blocker
    useBrowserPrompt(isUncommited);

    // Setup page navigation
    const [page, setPage] = useState<number>(0)

    // Navigate and save data
    const navigate = async (d: M, newPage?: number | boolean, submit?: boolean) => {
        setErrorText(null);
        setLoadingText("Saving...")
        try {
            if (!props.readOnly) {
                if (submit) {
                    d = await props.onSubmit(d);
                }
                else if(props.onSave != null){
                    d = await props.onSave(d);
                }
                onChange(d, true)
            }

            if (typeof newPage == "number") {
                setPage(newPage);
                window.scroll(0, 0);
            }
            else if (typeof newPage == "boolean" && newPage) {
                setTimeout(() => {
                    const postSubmitPage = props.onExit(d, submit ?? false);
                    if(typeof postSubmitPage === "number") {
                        setPage(postSubmitPage);
                    }
                }, 1);
            }

            return d;
        }
        catch (e: any) {
            setErrorText(e.message ? e.message : "Unexpected error");
        }
        finally {
            setLoadingText(null);
        }
        return null;
    }

    // Print data
    const print = async (d: M, format: FileDownloadFormats) => {

        try {
            setLoadingText("Downloading...")
            if(props.onPrint != null) {
                await props.onPrint(d, format)
            }
        }
        catch (e: any) {
            setErrorText(e.message ? e.message : "Unexpected error");
        }
        finally {
            setLoadingText(null);
        }
    }

    const ctx: FormContext<M> | null = model ? {
        model: model,
        additionalDownloadButtons: props.additionalDownloadButtons,
        readOnly: props.readOnly ? true : false,
        page: page,
        onSaveEnabled: props.onSave ? true: false,
        onNavigate: navigate,
        onChange: onChange,
        onError: setErrorText,
        onLoading: setLoadingText,
        getNextPage: props.getNextPage,
        getPreviousPage: props.getPreviousPage,
        onPrint: props.onPrint ? print : undefined
    } : null;

    // Render standard elements and current page
    return <>
        <Spinner isLoading={loadingText ? true : false} loadingText={loadingText || undefined}>
            {errorText ? <AlertProvider messages={[errorText]} dismiss={true} color="danger" /> : <></>}
            {props.progress ? <ProgressBar {...props.progress(page, model)} /> : <></>}
            <div id="form-container">
                {props.children && props.children(ctx)}
            </div>
        </Spinner>
        <RouterPromptModal block={isUncommited} />
    </>;
}